/**
 * @file ga4.js
 * @description Optional Google Analytics 4 loader for marketing analytics. GA4 is
 *   a SECONDARY sink — the in-house Supabase events table remains the source of
 *   truth (see lib/analytics.js). GA4 receives the same named conversion events
 *   (via window.gtag, forwarded from analytics.track) but ONLY the event name and
 *   explicitly-whitelisted non-PII params. We never send names, emails, phone
 *   numbers, exact addresses, or detailed role/credential/pay/availability — that
 *   data lives only in Supabase.
 *
 *   Entirely gated on REACT_APP_GA4_ID. With no id set (the default today), this
 *   is a complete no-op: no script loads, window.gtag never exists, and
 *   analytics.track simply skips the GA4 forward.
 * @module lib/ga4
 */

const GA4_ID = (process.env.REACT_APP_GA4_ID || "").trim();

/** True once a valid-looking GA4 Measurement ID is configured. */
export function ga4Enabled() {
  return /^G-[A-Z0-9]+$/i.test(GA4_ID);
}

/**
 * Forward a single event to GA4 via gtag. The one low-level GA4 send used by
 * every higher-level helper (analytics.track, analytics.trackGa4Only, and the
 * lifecycle mirror in eventService). No-op when GA4 isn't loaded (no id set) or
 * gtag isn't present. Params must be non-PII and low-cardinality — never pass
 * names, emails, phone, addresses, or raw ids like case_id/application_id.
 * Never throws.
 * @param {string} name  GA4 event name (prefer GA4-recommended names where they exist)
 * @param {Object} [params]  non-PII, low-cardinality params (e.g. { role: "rbt" })
 */
export function sendGa4(name, params = {}) {
  if (!name) return;
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params || {});
    }
  } catch {
    /* analytics must never break a caller */
  }
}

/**
 * The landing URL as GA4 should see it: origin + path + only utm_* params.
 * Every other query param and the hash are dropped, because some links carry
 * one-time secrets (unsubscribe / case-respond tokens, auth fragments) that
 * must never reach Google.
 * @param {string} href
 * @returns {string}
 */
export function ga4SafeLocation(href) {
  try {
    const u = new URL(href);
    const keep = new URLSearchParams();
    u.searchParams.forEach((v, k) => { if (/^utm_[a-z]+$/i.test(k)) keep.append(k, v); });
    const qs = keep.toString();
    return `${u.origin}${u.pathname}${qs ? `?${qs}` : ""}`;
  } catch {
    return undefined;
  }
}

let started = false;

/**
 * Load gtag.js and initialize GA4. Safe to call on every boot; only acts once,
 * and only when REACT_APP_GA4_ID is a valid GA4 id. No-op otherwise.
 */
export function initGa4() {
  if (started || !ga4Enabled() || typeof document === "undefined") return;
  started = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_ID)}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // Standard gtag shim — pushes raw arguments onto the dataLayer.
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // send_page_view stays on for basic traffic reporting; we never attach PII or
  // a user_id to the config, so GA4 only ever sees pseudonymous client data.
  //
  // Pin the landing URL, referrer and title NOW. gtag.js loads async and reads
  // the location when it processes the config, but by then the app may have
  // rewritten the URL (the /share/<id>?utm_* landing replaces itself with "/").
  // That recorded every share-link visit as the homepage with "(direct)" source,
  // hiding ads, social posts and outreach emails. initGa4 runs at boot, before
  // React renders, so these are still the real landing values.
  window.gtag("config", GA4_ID, {
    anonymize_ip:  true,
    page_location: ga4SafeLocation(window.location.href),
    page_referrer: document.referrer || undefined,
    page_title:    document.title || undefined,
  });
}
